import { subDays } from "date-fns";
import type { TFunction } from "i18next";

import { DateRangeQuickFilters } from "@/components/ui/v2/date-range-picker";
import { URL_PARAMS } from "@/constants/urlParams";
import type { ModuleUiColumnDefinition, UiComponent } from "@/features/integrations/types";
import type { WebAnalyticsFilterDimension, WebAnalyticsMetrics } from "@/features/web-analytics/types";
import type { ValueFormat } from "@/utils/valueFormat";

// ---------------------------------------------------------------------------
// Date defaults + selectable range
// ---------------------------------------------------------------------------

/** Default report window when no date params are in the URL (inclusive). */
export const DEFAULT_RANGE_DAYS = 7;

/**
 * Earliest selectable date — no Matomo data exists before this, so the picker shouldn't offer it.
 * Independent of reporting's floor: they happen to match today, but each tracks when its own source
 * started collecting.
 */
export const WEB_ANALYTICS_MIN_DATE = new Date(2026, 0, 1);

/**
 * Latest selectable date — yesterday, since today's data is still being ingested.
 * A function, not a constant: a module-level `new Date()` goes stale in a tab left open overnight.
 * `subDays`, not millisecond arithmetic: subtracting 86_400_000 crosses two calendar days when a DST
 * transition falls in between, which would silently move the ceiling a day.
 */
export function getWebAnalyticsMaxDate(): Date {
  return subDays(new Date(), 1);
}

// ---------------------------------------------------------------------------
// Date range picker
// ---------------------------------------------------------------------------

/**
 * Static picker options. `maxDate` is supplied per render by the page (see getWebAnalyticsMaxDate) so
 * it tracks the current day; it deliberately isn't set here.
 */
export const DATE_RANGE_PICKER_CONFIG = {
  minDate: WEB_ANALYTICS_MIN_DATE,
  allowedPresetIds: DateRangeQuickFilters.allExceptToday,
};

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

export const WEB_ANALYTICS_COLUMNS: ModuleUiColumnDefinition[] = [
  { key: "referrerType", label: "webAnalytics.columns.referrerType", type: "String" },
  { key: "trackingSource", label: "webAnalytics.columns.trackingSource", type: "String", showNullAs: "N/A" },
  { key: "platformSource", label: "webAnalytics.columns.platformSource", type: "String", showNullAs: "N/A" },
  { key: "visits", label: "webAnalytics.columns.visits", type: "Int", textAlign: "right" },
  { key: "bounced", label: "webAnalytics.columns.bounces", type: "Int", textAlign: "right" },
  { key: "bounceRate", label: "webAnalytics.columns.bounceRate", type: "Percentage", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "interactions", label: "webAnalytics.columns.interactions", type: "Int", textAlign: "right" },
  { key: "avgInteractions", label: "webAnalytics.columns.avgInteractions", type: "Float", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "conversionRate", label: "webAnalytics.columns.conversionRate", type: "Percentage", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "goalConversions", label: "webAnalytics.columns.totalConversions", type: "Int", textAlign: "right" },
];

/**
 * Max characters shown for the tracking/platform source columns before truncating with an ellipsis;
 * the full value is shown in a hover tooltip. UTM-style sources can run very long.
 */
export const SOURCE_COLUMN_MAX_LENGTH = 30;

/** Source columns that truncate long values with a hover tooltip (see WebAnalyticsPage). */
export const WEB_ANALYTICS_TRUNCATED_SOURCE_COLUMNS = ["trackingSource", "platformSource"] as const;

// ---------------------------------------------------------------------------
// Metric cards
// ---------------------------------------------------------------------------

type MetricCardConfig = {
  /** Headline value: metric field, format, and the card's title. */
  metricKey: keyof WebAnalyticsMetrics;
  labelKey: string;
  format: ValueFormat;
  /** Optional supporting total shown under the headline (e.g. the count behind a rate/average). */
  secondary?: {
    metricKey: keyof WebAnalyticsMetrics;
    format: ValueFormat;
    /** Interpolated with { total } → e.g. "1.234 interactions". */
    subtitleKey: string;
  };
};

/**
 * Top-of-table summary cards (data-only; the page maps each to an icon + formatted value). Cards 2-4
 * pair a rate/average headline with its underlying total as the subtitle.
 */
export const METRICS_CONFIG = [
  { metricKey: "totalVisits", labelKey: "webAnalytics.metrics.totalVisits", format: "whole", secondary: undefined },
  {
    metricKey: "averageInteractions",
    labelKey: "webAnalytics.metrics.averageInteractions",
    format: "decimal",
    secondary: { metricKey: "totalInteractions", format: "whole", subtitleKey: "webAnalytics.metrics.subtitles.interactions" },
  },
  {
    metricKey: "averageBounceRate",
    labelKey: "webAnalytics.metrics.averageBounceRate",
    format: "percentage",
    secondary: { metricKey: "totalBounces", format: "whole", subtitleKey: "webAnalytics.metrics.subtitles.bounces" },
  },
  {
    metricKey: "averageConversionRate",
    labelKey: "webAnalytics.metrics.averageConversionRate",
    format: "percentage",
    secondary: { metricKey: "totalConvertedVisits", format: "whole", subtitleKey: "webAnalytics.metrics.subtitles.convertedVisits" },
  },
  { metricKey: "totalConversions", labelKey: "webAnalytics.metrics.totalConversions", format: "whole", secondary: undefined },
] as const satisfies readonly MetricCardConfig[];

// ---------------------------------------------------------------------------
// Table toolbar
// ---------------------------------------------------------------------------

export const TABLE_TOOLBAR_CONFIG = {
  searchPlaceholderKey: "webAnalytics.searchPlaceholder" as const,
  dateRangeFilterKey: "dateRange" as const,
};

// ---------------------------------------------------------------------------
// Filter drawer
// ---------------------------------------------------------------------------

/** Filterable dimensions shown in the drawer; array order is the drawer section order. */
export const WEB_ANALYTICS_FILTER_DIMENSIONS: readonly WebAnalyticsFilterDimension[] = [
  "referrerType",
  "deviceType",
  "visitorType",
  "countryCode",
];

/** Params the drawer must keep intact when it rewrites the URL on apply. */
export const WEB_ANALYTICS_FILTER_PRESERVE_PARAM_KEYS = [
  URL_PARAMS.SEARCH,
  URL_PARAMS.ORDER_BY,
  URL_PARAMS.DESCENDING,
  URL_PARAMS.DATE_FROM,
  URL_PARAMS.DATE_TO,
];

// ---------------------------------------------------------------------------
// Page / search
// ---------------------------------------------------------------------------

export const PAGE_CONFIG = {
  titleKey: "webAnalytics.title" as const,
  subtitleKey: "webAnalytics.subtitle" as const,
  searchPlaceholderKey: "webAnalytics.searchPlaceholder" as const,
  entityNameKey: "webAnalytics.entityName" as const,
  noDataKey: "webAnalytics.noData" as const,
};

// ---------------------------------------------------------------------------
// UI component tree (table toolbar + data table)
// ---------------------------------------------------------------------------

/** Builds the website page UI component array (schema-driven render, mirroring General Reporting). */
export function getWebAnalyticsUiComponents(t: TFunction): UiComponent[] {
  return [
    {
      component: "tableToolbar",
      props: {
        search: {
          placeholder: t(TABLE_TOOLBAR_CONFIG.searchPlaceholderKey, "Search source or platform"),
          enabled: true,
        },
        filters: [
          {
            key: TABLE_TOOLBAR_CONFIG.dateRangeFilterKey,
            type: "DateRange",
            inlineWithSearch: true,
          },
        ],
      },
    },
    {
      component: "dataTable",
      // No rowIdColumn: a row's identity is the (referrerType, trackingSource, platformSource) grain,
      // not any single column, so DataTableComponent falls back to the row index for React keys. Safe
      // here — the table holds no per-row state and re-queries on every sort/page/filter change.
      props: {
        columns: WEB_ANALYTICS_COLUMNS,
      },
    },
  ];
}
